import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DocumentScanner from "@/components/DocumentScanner";
import { FileUp, Camera, Loader2, RefreshCw, AlertTriangle, CheckCircle2, FileText, Ban, Info } from "lucide-react";

const newOp = () => (crypto.randomUUID && crypto.randomUUID()) || `op_${Date.now()}_${Math.random().toString(36).slice(2)}`;
const money = (v) => (v == null ? "—" : `£${Number(v).toFixed(2)}`);
const STATUS = {
  new: ["New product", "bg-emerald-50 text-emerald-700"],
  price_change: ["Price change", "bg-amber-50 text-amber-700"],
  unchanged: ["Unchanged", "bg-slate-100 text-slate-500"],
  pack_change: ["Pack size changed", "bg-red-50 text-red-700"],
  possible_match: ["Possible match", "bg-violet-50 text-violet-700"],
  duplicate_in_import: ["Duplicate in list", "bg-slate-100 text-slate-500"],
  catalogue_linked: ["Catalogue-priced", "bg-blue-50 text-blue-700"],
};
const ACTIONS = {
  new: ["create", "skip"], price_change: ["update", "skip"], unchanged: ["update", "skip"],
  pack_change: ["update", "create", "skip"], possible_match: ["link", "create", "skip"],
  duplicate_in_import: ["skip"], catalogue_linked: ["skip"],
};
const ACTION_LABEL = { create: "Create new", update: "Update price", link: "Link to existing", skip: "Skip" };

function StatusPill({ status }) {
  const [label, cls] = STATUS[status] || [status, "bg-slate-100"];
  return <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded whitespace-nowrap ${cls}`}>{label}</span>;
}

function Start({ supplier, onJob }) {
  const [history, setHistory] = useState(null);
  const [scan, setScan] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { api.get(`/price-lists/imports?supplier_id=${supplier.supplier_id}`).then((r) => setHistory(r.data)).catch(() => setHistory([])); }, [supplier]);
  const create = async (file_ids) => {
    setBusy(true);
    try {
      const { data } = await api.post("/price-lists/imports", { supplier_id: supplier.supplier_id, file_ids });
      if (data.existing) toast.info("This exact price list was already imported — reopening it");
      onJob(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the import"); }
    finally { setBusy(false); }
  };
  const onPdf = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await create([data.file_id]);
    } catch (er) { toast.error(er.response?.data?.detail || "Upload failed"); setBusy(false); }
  };
  if (scan) return <DocumentScanner sessionKey={`pricelist-${supplier.supplier_id}`} maxPages={60} testid="pricelist-scanner" onComplete={(ids) => { setScan(false); create(ids); }} />;
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">Upload {supplier.name}'s price list. AI reads it in batches of pages; nothing changes in your catalogue until you review and approve.</p>
      <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onPdf} data-testid="pricelist-pdf-input" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button disabled={busy} onClick={() => fileRef.current?.click()} className="bg-brand hover:bg-brand-hover rounded-xl" data-testid="pricelist-upload-pdf-btn">
          {busy ? <Loader2 size={16} className="mr-1 animate-spin" /> : <FileUp size={16} className="mr-1" />} Upload PDF
        </Button>
        <Button disabled={busy} variant="outline" onClick={() => setScan(true)} className="rounded-xl" data-testid="pricelist-scan-btn"><Camera size={16} className="mr-1" /> Scan pages</Button>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Previous imports</div>
        {history === null ? <div className="text-xs text-slate-400">Loading…</div> : history.length === 0 ? <div className="text-xs text-slate-400" data-testid="pricelist-history-empty">None yet.</div> :
          <div className="space-y-1.5 max-h-48 overflow-y-auto">{history.map((h) => (
            <button key={h.import_id} onClick={() => onJob(h)} data-testid={`pricelist-history-${h.import_id}`} className="w-full text-left border rounded-lg p-2 text-xs hover:border-brand flex justify-between gap-2">
              <span>{new Date(h.created_at).toLocaleString()} · {h.page_count} page(s){h.partial ? " · PARTIAL" : ""}</span>
              <span className="font-semibold capitalize">{h.status}</span>
            </button>))}</div>}
      </div>
    </div>
  );
}

function Progress({ job, setJob }) {
  const [running, setRunning] = useState(true);
  const stop = useRef(false);
  useEffect(() => {
    stop.current = false;
    if (job.status !== "extracting" || !running) return undefined;
    (async () => {
      let cur = job, misses = 0;
      while (!stop.current && cur.status === "extracting") {
        try {
          const next = (await api.post(`/price-lists/imports/${job.import_id}/process-next`)).data;
          cur = next; misses = 0; setJob(next);
          if (cur.status === "extracting") await new Promise((r) => setTimeout(r, cur.throttled ? 15000 : 4000));
        }
        catch (e) {
          misses += 1; // server restart / network blip: keep polling, the server reclaims an abandoned batch
          if (misses > 30) { toast.error(e.response?.data?.detail || "Processing paused"); setRunning(false); break; }
          await new Promise((r) => setTimeout(r, 10000));
        }
      }
    })();
    return () => { stop.current = true; };
  }, [job.import_id, job.status, running]);
  const p = job.progress;
  const act = async (n, kind) => {
    try {
      if (kind === "exclude" && !window.confirm("Exclude these pages? The import will be marked PARTIAL and will not be a complete price list.")) return;
      const { data } = await api.post(`/price-lists/imports/${job.import_id}/batches/${n}/${kind}`, kind === "exclude" ? { confirm: true } : {});
      setJob(data); setRunning(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="pricelist-progress">
      <div className="flex justify-between text-sm"><span className="font-semibold">Reading pages {p.pages_done} / {p.pages_total}</span><span className="text-slate-500">{p.done}/{p.batches_total} batches</span></div>
      <div className="h-2 rounded bg-slate-100 overflow-hidden"><div className="h-full bg-brand transition-[width] duration-500" style={{ width: `${(p.pages_done / p.pages_total) * 100}%` }} data-testid="pricelist-progress-bar" /></div>
      {job.status === "extracting" && p.pages_done === 0 && <div className="text-xs text-slate-500" data-testid="pricelist-first-batch-hint">Reading the first pages — dense price lists take about a minute per batch.</div>}
      {job.status === "extracting" && <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 size={13} className="animate-spin" /> Pages are read on the server — you can close this window and reopen the import later.</div>}
      {!running && job.status === "extracting" && <Button size="sm" variant="outline" onClick={() => setRunning(true)} data-testid="pricelist-resume-btn">Resume</Button>}
      <div className="space-y-1 max-h-56 overflow-y-auto">
        {job.batches.filter((b) => b.status === "failed" || b.status === "excluded").map((b) => (
          <div key={b.n} className="flex items-center justify-between gap-2 border rounded-lg p-2 text-xs" data-testid={`pricelist-batch-${b.n}`}>
            <span className={b.status === "failed" ? "text-red-600" : "text-slate-500"}>Pages {b.first_page}–{b.last_page}: {b.status === "failed" ? (b.error?.message || "failed") : "excluded"}</span>
            {b.status === "failed" && <span className="flex gap-1">
              <Button size="sm" variant="outline" className="h-7" onClick={() => act(b.n, "retry")} data-testid={`pricelist-retry-${b.n}`}><RefreshCw size={12} className="mr-1" />Retry</Button>
              <Button size="sm" variant="ghost" className="h-7 text-slate-500" onClick={() => act(b.n, "exclude")} data-testid={`pricelist-exclude-${b.n}`}><Ban size={12} className="mr-1" />Exclude</Button>
            </span>}
          </div>))}
      </div>
      {job.status === "incomplete" && <div className="text-xs rounded-lg bg-red-50 text-red-700 p-2" data-testid="pricelist-incomplete">Some pages failed. Retry them, or explicitly exclude them, before you can review — an incomplete import is never treated as a full price list.</div>}
    </div>
  );
}

function RowEditor({ row, d, setD }) {
  const m = row.match || {};
  const e = d.edits || {};
  const val = (k, fallback) => (k in e ? e[k] : fallback);
  const setE = (k, v) => setD({ ...d, edits: { ...e, [k]: v } });
  const done = row.state !== "pending";
  const pct = m.pct_change;
  return (
    <div className={`border rounded-lg p-2 text-xs space-y-1.5 ${done ? "opacity-60" : ""}`} data-testid={`pricelist-row-${row.row_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-slate-400 font-mono" data-testid={`pricelist-row-page-${row.row_id}`}>p.{row.page}</span>
        <StatusPill status={row.status} />
        {m.significant && <span className="text-[10px] font-bold text-red-600 flex items-center gap-0.5"><AlertTriangle size={11} />Needs confirmation</span>}
        {row.attention?.includes("suspicious_price") && <span className="text-[10px] font-bold text-red-600" data-testid={`pricelist-suspicious-${row.row_id}`}>Suspicious price — confirm</span>}
        {row.unit_prices && Object.keys(row.unit_prices).length > 1 && <span className="text-[10px] text-slate-500">{Object.entries(row.unit_prices).map(([b, v]) => `${{ case: "Box", kg: "Kilo", each: "Each" }[b] || b} ${money(v)}`).join(" · ")}</span>}
        {row.attention?.length > 0 && <span className="text-[10px] text-amber-700">Check: {row.attention.join(", ").replace(/_/g, " ")}</span>}
        {done && <span className="ml-auto font-semibold text-emerald-700 flex items-center gap-1"><CheckCircle2 size={12} />{row.state}{row.applied?.action ? ` (${row.applied.action})` : ""}</span>}
      </div>
      <div className="grid grid-cols-12 gap-1.5">
        <Input disabled={done} className="col-span-3 h-8 text-xs font-mono" value={val("sku", row.sku)} onChange={(x) => setE("sku", x.target.value)} placeholder="Code" data-testid={`pricelist-sku-${row.row_id}`} />
        <Input disabled={done} className="col-span-9 h-8 text-xs" value={val("description", row.description)} onChange={(x) => setE("description", x.target.value)} placeholder="Description" data-testid={`pricelist-desc-${row.row_id}`} />
        <Input disabled={done} className="col-span-4 h-8 text-xs" value={val("pack_size", row.pack?.printed)} onChange={(x) => setE("pack_size", x.target.value)} placeholder="Pack e.g. 5 x 1kg" data-testid={`pricelist-pack-${row.row_id}`} />
        <Input disabled={done} className="col-span-3 h-8 text-xs" value={val("price", row.price ?? "")} onChange={(x) => setE("price", x.target.value)} placeholder="Price" data-testid={`pricelist-price-${row.row_id}`} />
        <Input disabled={done} className="col-span-2 h-8 text-xs" value={val("vat_rate", row.vat_rate ?? "")} onChange={(x) => setE("vat_rate", x.target.value)} placeholder="VAT%" data-testid={`pricelist-vat-${row.row_id}`} />
        <Input disabled={done} className="col-span-3 h-8 text-xs" type="date" value={val("effective_date", row.effective_date || "")} onChange={(x) => setE("effective_date", x.target.value)} data-testid={`pricelist-eff-${row.row_id}`} />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {m.product_name && <span className="text-slate-500" data-testid={`pricelist-compare-${row.row_id}`}>Current: {m.product_name} · {money(m.current_price)} {m.current_pack && `· ${m.current_pack}`}
          {pct != null && <b className={pct > 0 ? "text-red-600" : "text-emerald-700"}> {pct > 0 ? "increase +" : "decrease "}{pct}%</b>}</span>}
        {row.pack?.costing_pack_size && <span className="text-slate-400">Costed as {row.pack.costing_pack_size}{row.min_order_qty != null ? ` · MOQ ${row.min_order_qty}` : ""}</span>}
        {row.effective_date && row.effective_date > new Date().toISOString().slice(0, 10) && <span className="text-blue-700 font-semibold">Future price — scheduled from {row.effective_date}</span>}
      </div>
      {row.assumptions?.length > 0 && <div className="text-[11px] text-slate-500 flex gap-1" data-testid={`pricelist-assumptions-${row.row_id}`}><Info size={12} className="shrink-0 mt-0.5" /><span>Assumed: {row.assumptions.join("; ")}</span></div>}
      {!done && <div className="flex items-center gap-2 flex-wrap">
        <Select value={d.action || ""} onValueChange={(v) => setD({ ...d, action: v })}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid={`pricelist-action-${row.row_id}`}><SelectValue placeholder="Choose action" /></SelectTrigger>
          <SelectContent>{(ACTIONS[row.status] || ["skip"]).map((a) => <SelectItem key={a} value={a}>{ACTION_LABEL[a]}</SelectItem>)}</SelectContent>
        </Select>
        {d.action === "link" && <Select value={d.product_id || ""} onValueChange={(v) => setD({ ...d, product_id: v })}>
          <SelectTrigger className="h-8 flex-1 min-w-[180px] text-xs" data-testid={`pricelist-link-${row.row_id}`}><SelectValue placeholder="Pick existing product" /></SelectTrigger>
          <SelectContent>{(m.candidates || []).filter((c) => !c.global_linked).map((c) => <SelectItem key={c.product_id} value={c.product_id}>{c.name} {c.sku ? `(${c.sku})` : ""} · {c.pack_size || "no pack"} · {money(c.price)}{c.pack_differs ? " · DIFFERENT PACK" : ""}</SelectItem>)}</SelectContent>
        </Select>}
      </div>}
    </div>
  );
}

function RecipeImpact({ importId, refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (refreshKey) api.get(`/price-lists/imports/${importId}/recipe-impact`).then((r) => setData(r.data)).catch(() => setData(null)); }, [importId, refreshKey]);
  if (!data || !data.recipes.length) return null;
  return (
    <details open className="text-xs rounded-lg border p-2" data-testid="pricelist-recipe-impact">
      <summary className="cursor-pointer font-semibold">{data.recipes.length} recipe(s) affected by approved price changes (read-only — recipes and selling prices are not changed)</summary>
      <div className="mt-1 space-y-1 max-h-48 overflow-y-auto">{data.recipes.map((r) => (
        <div key={r.recipe_id} className="flex justify-between gap-2" data-testid={`pricelist-impact-${r.recipe_id}`}>
          <span>{r.name}</span>
          <span>{money(r.cost_per_portion_before)} → {money(r.cost_per_portion_after)}/portion{r.gp_change_points != null && <b className={r.gp_change_points < 0 ? "text-red-600" : "text-emerald-700"}> · GP {r.gp_percent_before}% → {r.gp_percent_after}%</b>}</span>
        </div>))}</div>
    </details>
  );
}

const FILTERS = ["all", "new", "price_change", "pack_change", "possible_match", "unchanged", "duplicate_in_import", "catalogue_linked"];

function Review({ job, onApproved }) {
  const [rows, setRows] = useState(null);
  const [dec, setDec] = useState({});
  const [filter, setFilter] = useState("all");
  const [ackPartial, setAckPartial] = useState(false);
  const [confirmRows, setConfirmRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    let all = [], skip = 0;
    for (;;) { const { data } = await api.get(`/price-lists/imports/${job.import_id}/rows?skip=${skip}&limit=1000`); all = all.concat(data); if (data.length < 1000) break; skip += 1000; }
    setRows(all);
    setDec(Object.fromEntries(all.filter((r) => r.state === "pending").map((r) => [r.row_id, { action: r.default_action || null }])));
  };
  useEffect(() => { load(); }, [job.import_id]);
  if (!rows) return <div className="text-sm text-slate-400">Loading rows…</div>;
  const shown = rows.filter((r) => filter === "all" || r.status === filter);
  const count = (s) => rows.filter((r) => s === "all" || r.status === s).length;
  const undecided = rows.filter((r) => r.state === "pending" && !dec[r.row_id]?.action).length;
  const submit = async (confirmIds = []) => {
    const decisions = rows.filter((r) => r.state === "pending" && dec[r.row_id]?.action).map((r) => {
      const d = dec[r.row_id];
      return { row_id: r.row_id, action: d.action, product_id: d.product_id, edits: d.edits, confirm: d.confirm || confirmIds.includes(r.row_id) };
    });
    if (!decisions.length) return toast.error("Choose an action for at least one row");
    if (decisions.some((d) => d.action === "link" && !d.product_id)) return toast.error("Pick the existing product for every 'Link' row");
    setBusy(true);
    try {
      for (let i = 0; i < decisions.length; i += 500) {
        await api.post(`/price-lists/imports/${job.import_id}/approve`, { operation_id: newOp(), decisions: decisions.slice(i, i + 500), acknowledge_partial: ackPartial });
      }
      toast.success("Approved changes applied"); setConfirmRows(null); await load(); onApproved();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det?.code === "confirmation_required") setConfirmRows(det.rows);
      else if (det?.code === "invalid_decisions") toast.error(`Some rows can't be applied: ${det.rows.map((r) => r.code.replace(/_/g, " ")).join(", ")}`);
      else toast.error(det?.message || det || "Approval failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="pricelist-review">
      {job.partial && <label className="flex items-start gap-2 text-xs rounded-lg bg-amber-50 text-amber-800 p-2" data-testid="pricelist-partial-warning">
        <Checkbox checked={ackPartial} onCheckedChange={(v) => setAckPartial(!!v)} data-testid="pricelist-partial-ack" />
        PARTIAL import — pages {job.excluded_pages.join(", ")} were excluded. I understand this is not the complete price list.</label>}
      {job.not_in_list?.length > 0 && <details className="text-xs rounded-lg bg-slate-50 p-2" data-testid="pricelist-not-in-list">
        <summary className="cursor-pointer font-semibold">{job.not_in_list.length} of your products for this supplier are not on this list (possibly discontinued) — nothing is changed automatically</summary>
        <div className="mt-1 max-h-32 overflow-y-auto">{job.not_in_list.map((p) => <div key={p.product_id}>{p.sku} · {p.name} · {money(p.price)}</div>)}</div>
      </details>}
      <RecipeImpact importId={job.import_id} refreshKey={rows.filter((r) => r.state === "applied").length} />
      {job.assumptions?.length > 0 && <div className="text-[11px] text-slate-500 bg-slate-50 rounded-lg p-2" data-testid="pricelist-doc-assumptions">AI assumptions: {job.assumptions.join(" · ")}</div>}
      <div className="flex gap-1 flex-wrap">{FILTERS.filter((f) => f === "all" || count(f)).map((f) => (
        <button key={f} onClick={() => setFilter(f)} data-testid={`pricelist-filter-${f}`} className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${filter === f ? "bg-brand text-white border-brand" : "bg-white text-slate-600"}`}>
          {f === "all" ? "All" : STATUS[f][0]} ({count(f)})</button>))}</div>
      <div className="space-y-2 max-h-[48vh] overflow-y-auto pr-1">
        {shown.length === 0 ? <div className="text-sm text-slate-400" data-testid="pricelist-no-rows">No products found in this filter.</div> :
          shown.map((r) => <RowEditor key={r.row_id} row={r} d={dec[r.row_id] || {}} setD={(v) => setDec((s) => ({ ...s, [r.row_id]: v }))} />)}
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs text-slate-500" data-testid="pricelist-undecided">{undecided} row(s) still need a decision</span>
        <Button disabled={busy || (job.partial && !ackPartial)} onClick={() => submit()} className="bg-brand hover:bg-brand-hover rounded-xl" data-testid="pricelist-approve-btn">
          {busy && <Loader2 size={15} className="mr-1 animate-spin" />} Approve selected changes</Button>
      </div>
      {confirmRows && <Dialog open onOpenChange={() => setConfirmRows(null)}>
        <DialogContent data-testid="pricelist-confirm-modal" className="max-w-lg">
          <DialogHeader><DialogTitle>Confirm significant changes</DialogTitle><DialogDescription>These rows raise the price by more than 10% or change the pack size.</DialogDescription></DialogHeader>
          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto text-xs">{confirmRows.map((c) => (
            <div key={c.row_id} className="border rounded-lg p-2" data-testid={`pricelist-confirm-${c.row_id}`}>
              <div className="font-semibold">p.{c.page} · {c.description}</div>
              <div>{money(c.old_price)} → {money(c.new_price)} {c.pct_change != null && <b className="text-red-600">({c.pct_change > 0 ? "+" : ""}{c.pct_change}%)</b>}</div>
              {c.pack_change && <div className="text-red-700">Pack: {c.old_pack || "—"} → {c.new_pack || "—"}</div>}
            </div>))}</div>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmRows(null)}>Back to review</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={() => submit(confirmRows.map((c) => c.row_id))} data-testid="pricelist-confirm-btn">Confirm and apply</Button></DialogFooter>
        </DialogContent>
      </Dialog>}
    </div>
  );
}

export default function PriceListImport({ supplier, onClose, onApproved }) {
  const [job, setJob] = useState(null);
  const open = async (j) => { try { setJob((await api.get(`/price-lists/imports/${j.import_id}`)).data); } catch { setJob(j); } };
  const reviewable = job && ["review", "completed"].includes(job.status);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="pricelist-modal" className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText size={18} className="text-brand" /> Import price list · {supplier.name}</DialogTitle>
          <DialogDescription>AI-suggested and unverified — review every row before approving.</DialogDescription>
        </DialogHeader>
        {!job && <Start supplier={supplier} onJob={open} />}
        {job && <div className="flex items-center justify-between text-xs text-slate-500 gap-2 flex-wrap">
          <span data-testid="pricelist-job-status">{job.page_count} page(s) · status <b className="capitalize">{job.status}</b>{job.partial ? " · PARTIAL" : ""}</span>
          <span className="flex gap-3">{job.combined_file_id && <a className="text-brand underline" href={`${api.defaults.baseURL}/files/${job.combined_file_id}?auth=${localStorage.getItem("cc_token")}`} target="_blank" rel="noreferrer" data-testid="pricelist-source-link">Source PDF</a>}
            <button className="text-brand underline" onClick={() => setJob(null)} data-testid="pricelist-back-btn">All imports</button></span>
        </div>}
        {job && !reviewable && <Progress job={job} setJob={setJob} />}
        {reviewable && <Review job={job} onApproved={() => { open(job); onApproved?.(); }} />}
      </DialogContent>
    </Dialog>
  );
}
